import classNames from 'classnames';
import { useField, useFormikContext } from 'formik';
import { useCallback } from 'react';
import { Button } from 'yoso';
import {
  useChallengeScore,
  useScoreUpCriteria,
} from '~/components/Challenge/Score/ChallengeScore.hooks';
import { ChallengeScoreUpValues } from '~/components/Challenge/Score/Form/ChallengeScoreUpForm.hooks';
import { SpinNumberInput } from '~/components/Form/Number/Spin/SpinNumberInput';
import { T } from '~/components/I18n';
import { AutoSizeText } from '~/components/Text/AutoSize/AutoSizeText';
import { format, formatDon } from '~/components/Util/number';
import styles from './ScoreUpCountField.module.scss';

export type ScoreUpCountFieldProps = {
  name?: string;
  className?: string;
  autoFocus?: boolean;
};
export const ScoreUpCountField = ({
  name = 'count',
  className,
  autoFocus,
}: ScoreUpCountFieldProps) => {
  const {
    values: { balance },
  } = useFormikContext<ChallengeScoreUpValues>();
  const [field, meta, { setValue }] = useField({
    type: 'number',
    name,
  });

  const {
    data: { scoreUpMaxCount, scoreUpCount },
  } = useChallengeScore();
  const {
    data: { joinForAssetAmount, maxCountPerJoin, assetType },
  } = useScoreUpCriteria();

  const remainingCount = Math.min(Math.max(scoreUpMaxCount - scoreUpCount, 0), maxCountPerJoin);
  const availableCount = Math.min(
    Math.floor(balance / joinForAssetAmount),
    remainingCount,
    maxCountPerJoin
  );
  const onChange = useCallback(
    (e) => {
      setValue(Number(e.currentTarget.value));
    },
    [setValue]
  );

  const disableMaxButton =
    balance < joinForAssetAmount || field.value === availableCount || remainingCount === 0;

  const onMaxButtonClick = useCallback(() => {
    setValue(availableCount);
  }, [availableCount, setValue]);

  return (
    <div className={classNames(styles.root, className, { [styles.error]: meta.error })}>
      <div className={styles.body}>
        <strong className={styles.don}>
          <AutoSizeText>
            {`${formatDon(field.value * joinForAssetAmount)}${assetType}`}
          </AutoSizeText>
        </strong>
        <SpinNumberInput
          className={styles.input}
          mask="999999"
          min={1}
          max={availableCount}
          autoFocus={autoFocus}
          {...field}
          onChange={onChange}
        />
      </div>
      <div className={styles.footer}>
        <Button
          className={styles.maxButton}
          variant="text"
          disabled={disableMaxButton}
          onClick={onMaxButtonClick}
          data-event="click"
          data-event-target="apply_max_btn">
          <T name="citizen_challenge_scoreup_popup_max_item" />
        </Button>
        <p className={styles.message}>
          {(() => {
            switch (meta.error) {
              case 'NOT_ENOUGH_BALANCE':
                return (
                  <>
                    <T name="citizen_don_in_possession" />: {format(balance)}
                  </>
                );
              case 'MAX_COUNT':
                return (
                  <>
                    <T
                      name="citizen_challenge_scoreup_popup_error"
                      options={[format(remainingCount)]}
                    />
                  </>
                );
              default:
                return (
                  <>
                    <T name="citizen_don_in_possession" />: {format(balance)}
                  </>
                );
            }
          })()}
        </p>
      </div>
    </div>
  );
};
